import { useQuery } from "@tanstack/react-query";
import { getGames, LEAGUE_IDS, type Game as ApiGame } from "@/lib/apiSports";
import { getCachedData, setCachedData, generateCacheKey, CACHE_TTL } from "@/lib/cache";

type League = "NHL" | "AHL" | "ECHL" | "KHL" | "SHL" | "Allsvenskan" | "Liiga" | "Extraliga" | "DEL" | "NL" | "PWHL" | "SDHL" | "Naisten Liiga";

// Batch size and delay to prevent rate limiting
const BATCH_SIZE = 3;
const BATCH_DELAY_MS = 200;

// Helper to batch requests with delay
async function batchedFetch<T>(
  items: number[],
  fetchFn: (item: number) => Promise<T>
): Promise<PromiseSettledResult<T>[]> {
  const results: PromiseSettledResult<T>[] = [];
  
  for (let i = 0; i < items.length; i += BATCH_SIZE) {
    const batch = items.slice(i, i + BATCH_SIZE);
    const batchResults = await Promise.allSettled(batch.map(fetchFn));
    results.push(...batchResults);
    
    // Add delay between batches (except after the last batch)
    if (i + BATCH_SIZE < items.length) {
      await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
    }
  }
  
  return results;
}

export interface PeriodScores {
  first: { home: number | null; away: number | null };
  second: { home: number | null; away: number | null };
  third: { home: number | null; away: number | null };
  overtime: { home: number | null; away: number | null };
  penalties: { home: number | null; away: number | null };
}

export interface ScheduledGame {
  id: number;
  date: string;
  time: string;
  timestamp: number;
  homeTeam: string;
  awayTeam: string;
  homeScore: number | null;
  awayScore: number | null;
  status: string;
  statusShort: string;
  isLive: boolean;
  isFinished: boolean;
  isScheduled: boolean;
  homeAbbr: string;
  awayAbbr: string;
  homeLogo: string;
  awayLogo: string;
  league: League;
  leagueName: string;
  leagueLogo: string;
  country: string;
  countryFlag: string;
  periods: PeriodScores;
  venue?: string;
  tvChannels: string[];
}

function extractTeamAbbr(teamName: string): string {
  const abbreviations: Record<string, string> = {
    "Boston Bruins": "BOS", "Toronto Maple Leafs": "TOR", "Montreal Canadiens": "MTL",
    "New York Rangers": "NYR", "Detroit Red Wings": "DET", "Chicago Blackhawks": "CHI",
    "Pittsburgh Penguins": "PIT", "Philadelphia Flyers": "PHI", "Washington Capitals": "WSH",
    "Tampa Bay Lightning": "TBL", "Florida Panthers": "FLA", "Carolina Hurricanes": "CAR",
    "New Jersey Devils": "NJD", "New York Islanders": "NYI", "Columbus Blue Jackets": "CBJ",
    "Buffalo Sabres": "BUF", "Ottawa Senators": "OTT", "Colorado Avalanche": "COL",
    "Dallas Stars": "DAL", "Minnesota Wild": "MIN", "St. Louis Blues": "STL",
    "Nashville Predators": "NSH", "Winnipeg Jets": "WPG", "Vegas Golden Knights": "VGK",
    "Edmonton Oilers": "EDM", "Calgary Flames": "CGY", "Vancouver Canucks": "VAN",
    "Seattle Kraken": "SEA", "San Jose Sharks": "SJS", "Los Angeles Kings": "LAK",
    "Anaheim Ducks": "ANA", "Arizona Coyotes": "ARI", "Utah Hockey Club": "UTA",
  };
  return abbreviations[teamName] || teamName.substring(0, 3).toUpperCase();
}

function getLeagueFromId(leagueId: number): League {
  const reverseMap: Record<number, League> = {
    [LEAGUE_IDS.NHL]: "NHL",
    [LEAGUE_IDS.AHL]: "AHL",
    [LEAGUE_IDS.ECHL]: "ECHL",
    [LEAGUE_IDS.KHL]: "KHL",
    [LEAGUE_IDS.SHL]: "SHL",
    [LEAGUE_IDS.ALLSVENSKAN]: "Allsvenskan",
    [LEAGUE_IDS.LIIGA]: "Liiga",
    [LEAGUE_IDS.EXTRALIGA]: "Extraliga",
    [LEAGUE_IDS.DEL]: "DEL",
    [LEAGUE_IDS.NL]: "NL",
    [LEAGUE_IDS.PWHL]: "PWHL",
    [LEAGUE_IDS.SDHL]: "SDHL",
    [LEAGUE_IDS.NAISTEN_LIIGA]: "Naisten Liiga",
  };
  return reverseMap[leagueId] || "NHL";
}

function transformToScheduledGame(game: ApiGame): ScheduledGame {
  const statusLower = game.status.short.toUpperCase();
  const isLive = ["1P", "2P", "3P", "OT", "P1", "P2", "P3", "LIVE", "BT", "INT"].includes(statusLower);
  const isFinished = ["FT", "AOT", "AP", "POST"].includes(statusLower);
  const isScheduled = ["NS", "TBD", "CANC", "PST", "ABD"].includes(statusLower);

  // Generate mock broadcast channels based on league
  const getBroadcastChannels = (league: League): string[] => {
    const broadcasts: Record<League, string[]> = {
      NHL: ["ESPN", "TNT", "ESPN+"],
      AHL: ["AHL TV", "FloHockey"],
      ECHL: ["FloHockey"],
      KHL: ["KHL TV", "Match TV"],
      SHL: ["C More Hockey", "TV4 Play"],
      Allsvenskan: ["C More Hockey", "TV4 Play"],
      Liiga: ["Elisa Viihde Viaplay", "C More"],
      Extraliga: ["O2 TV Sport", "Nova Sport"],
      DEL: ["MagentaSport", "Sport1"],
      NL: ["MySports One", "MySports Two"],
      PWHL: ["TSN", "ESPN+"],
      SDHL: ["C More Hockey", "SVT Play"],
      "Naisten Liiga": ["Elisa Viihde Viaplay", "Ruutu+"],
    };
    return broadcasts[league] || [];
  };

  const leagueType = getLeagueFromId(game.league.id);

  // Derive local time from the UTC date string so it reflects the user's timezone
  const parsedDate = new Date(game.date);
  const localTime = !isNaN(parsedDate.getTime())
    ? parsedDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })
    : game.time || "TBD";

  return {
    id: game.id,
    date: game.date,
    time: localTime,
    timestamp: game.timestamp,
    homeTeam: game.teams.home.name,
    awayTeam: game.teams.away.name,
    homeScore: game.scores.home,
    awayScore: game.scores.away,
    status: game.status.long,
    statusShort: game.status.short,
    isLive,
    isFinished,
    isScheduled,
    homeAbbr: extractTeamAbbr(game.teams.home.name),
    awayAbbr: extractTeamAbbr(game.teams.away.name),
    homeLogo: game.teams.home.logo || "",
    awayLogo: game.teams.away.logo || "",
    league: leagueType,
    leagueName: game.league.name,
    leagueLogo: game.league.logo || "",
    country: game.country?.name || "",
    countryFlag: game.country?.flag || "",
    periods: game.periods,
    venue: `${game.teams.home.name} Arena`,
    tvChannels: getBroadcastChannels(leagueType),
  };
}

export function useScheduleForDate(date: string, leagues?: League[]) {
  const leagueIds = leagues?.length 
    ? leagues.map(l => LEAGUE_IDS[l.toUpperCase() as keyof typeof LEAGUE_IDS])
    : Object.values(LEAGUE_IDS);

  return useQuery({
    queryKey: ["schedule", date, leagueIds.sort().join(",")],
    queryFn: async () => {
      const cacheKey = generateCacheKey("schedule", { date, leagues: leagueIds.sort().join(",") });
      const cached = getCachedData<ScheduledGame[]>(cacheKey);
      if (cached) {
        console.log("Using cached schedule for", date);
        return cached;
      }

      console.log("Fetching schedule for", date);
      // Hockey seasons span two years (e.g., 2025-2026). Use the starting year.
      // If we're in months Aug-Dec, use current year. If Jan-Jul, use previous year.
      const now = new Date();
      const month = now.getMonth(); // 0-indexed, Jan=0
      const seasonYear = month >= 7 ? now.getFullYear() : now.getFullYear() - 1;
      
      const results = await batchedFetch(leagueIds, (leagueId) =>
        getGames({ date, league: leagueId, season: seasonYear })
      );

      const allGames: ScheduledGame[] = [];
      
      for (const result of results) {
        if (result.status === "fulfilled" && result.value.response) {
          allGames.push(...result.value.response.map(transformToScheduledGame));
        }
      }

      // Sort by time
      const sortedGames = allGames.sort((a, b) => {
        if (a.isLive && !b.isLive) return -1;
        if (!a.isLive && b.isLive) return 1;
        return a.time.localeCompare(b.time);
      });

      // Use longer cache for future dates, shorter for today
      const today = new Date().toISOString().split("T")[0];
      const ttl = date === today ? CACHE_TTL.LIVE_GAMES : CACHE_TTL.STANDINGS;
      setCachedData(cacheKey, sortedGames, ttl);
      
      return sortedGames;
    },
    staleTime: 60 * 1000,
    retry: 2,
  });
}

export function useWeekSchedule(startDate: Date) {
  const dates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return date.toISOString().split("T")[0];
  });

  return useQuery({
    queryKey: ["weekSchedule", dates[0]],
    queryFn: async () => {
      const cacheKey = `weekSchedule_${dates[0]}`;
      const cached = getCachedData<Record<string, ScheduledGame[]>>(cacheKey);
      if (cached) {
        console.log("Using cached week schedule");
        return cached;
      }

      console.log("Fetching week schedule starting", dates[0]);
      const leagueIds = Object.values(LEAGUE_IDS);
      // Hockey seasons span two years. Use the starting year of the current season.
      const now = new Date();
      const month = now.getMonth();
      const seasonYear = month >= 7 ? now.getFullYear() : now.getFullYear() - 1;
      
      const scheduleByDate: Record<string, ScheduledGame[]> = {};
      
      // Fetch dates sequentially to avoid rate limits, but batch leagues within each date
      for (const date of dates) {
        const leagueResults = await batchedFetch(leagueIds, (leagueId) =>
          getGames({ date, league: leagueId, season: seasonYear })
        );
        
        const games: ScheduledGame[] = [];
        for (const result of leagueResults) {
          if (result.status === "fulfilled" && result.value.response) {
            games.push(...result.value.response.map(transformToScheduledGame));
          }
        }
        
        scheduleByDate[date] = games.sort((a, b) => a.time.localeCompare(b.time));
      }


      setCachedData(cacheKey, scheduleByDate, CACHE_TTL.STANDINGS);
      return scheduleByDate;
    },
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

export function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);
  
  if (dateOnly.getTime() === today.getTime()) return "Today";
  if (dateOnly.getTime() === tomorrow.getTime()) return "Tomorrow";
  
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
